import type {
  ImportedAllergyLineResponse,
  ImportedMedicationLineResponse,
} from '../../services/api';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { ALREADY_HANDLED_RELOAD_COPY } from './importedChartLineCopy';

/**
 * The reconcile states a line can be in. Mirrors the backend enum
 * (`ImportedLineReconcileStatus`); the column is a plain string there so a
 * fourth state can be added without a type migration, which means the frontend
 * must not assume these three are exhaustive when *hiding* rows — only when
 * deciding what is still outstanding.
 */
export const RECONCILE_STATUS_RAW = 'raw';

/** The importer's own conversion of a certain match. */
export const RECONCILE_STATUS_AUTO = 'auto_reconciled';

/**
 * Lines still waiting on a clinician's ruling.
 *
 * The display filter for every imported surface, and the count behind the
 * importer tool button. A reconciled or declined line is not deleted — its
 * provenance chain outlives the ruling — but it has stopped being something
 * anybody needs to act on, so it leaves the working list.
 *
 * Deliberately "status is raw" rather than "status is not reconciled and not
 * not_carried": a state this client has never heard of should read as *ruled
 * on* and drop out, not reappear as outstanding work an older frontend cannot
 * describe.
 */
export function outstandingLines<
  T extends ImportedMedicationLineResponse | ImportedAllergyLineResponse,
>(lines: T[] | undefined | null): T[] {
  return (lines ?? []).filter(
    line => line.reconcile_status === RECONCILE_STATUS_RAW
  );
}

/**
 * Lines the import coded on its own, from a certain Medi-Span to FDB match.
 *
 * Not outstanding: each is already a coded record on the chart. The Importer
 * tool lists them apart so a clinician can see the match and revert it.
 */
export function convertedLines<
  T extends ImportedMedicationLineResponse | ImportedAllergyLineResponse,
>(lines: T[] | undefined | null): T[] {
  return (lines ?? []).filter(
    line => line.reconcile_status === RECONCILE_STATUS_AUTO
  );
}

/**
 * Toast copy for a failed ruling.
 *
 * A 409 with `IMPORTED_LINE_ALREADY_RULED` means another clinician or tab
 * settled the record after this list loaded, so the useful next step is a
 * reload, not a retry. Keyed off the stable code, never the detail string.
 */
export function rulingErrorMessage(error: unknown, fallback: string): string {
  if (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    (error as { code?: unknown }).code === 'IMPORTED_LINE_ALREADY_RULED'
  ) {
    return ALREADY_HANDLED_RELOAD_COPY;
  }
  return getBusinessErrorMessage(error) ?? fallback;
}
